/**
 * FM profile-completeness / skill-endorsement helpers (F2).
 * Parity of `fmforecastview.html` (the PROFILE COMPLETENESS endorsement legend + the
 * three-state "Skills Last Updated" cell) and `fmdashboard.controller.js` `endorsedStatus`
 * handling. Pure/side-effect-free so it can be unit-tested and reused without the FXP shell.
 */

/** One entry in the profile-completeness endorsement legend dropdown. */
export interface EndorsementLegendEntry {
  key: string;
  label: string;
  /** Fluent UI icon name. */
  iconName: string;
  color: string;
}

/** The endorsement legend (parity: `fmforecastview.html` lines 16-20). */
export const ENDORSEMENT_LEGEND: EndorsementLegendEntry[] = [
  { key: 'notEndorsed', label: 'Not Yet Endorsed', iconName: 'StatusErrorFull', color: '#a4262c' },
  { key: 'within180', label: 'Endorsed within 180 days', iconName: 'CompletedSolid', color: '#107c10' },
  { key: 'between180and360', label: 'Endorsed between 180-360 days', iconName: 'AlertSolid', color: '#c19c00' },
  { key: 'over360', label: 'Endorsed more than 360 days ago', iconName: 'WarningSolid', color: '#d83b01' },
  { key: 'notApplicable', label: 'Not Applicable', iconName: 'Blocked2Solid', color: '#605e5c' },
];

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** Format a date as `dd-MMM-yyyy` (parity of the source `date:'dd-MMM-yyyy'` filter). */
export function formatSkillsDate(value?: string | null): string {
  if (!value) {
    return '';
  }
  const d = new Date(value);
  if (isNaN(d.getTime())) {
    return String(value);
  }
  const dd = String(d.getDate()).padStart(2, '0');
  return `${dd}-${MONTHS[d.getMonth()]}-${d.getFullYear()}`;
}

/**
 * The three-state "Skills Last Updated" cell value (parity: `fmforecastview.html` lines 102-104):
 *  - a formatted date when `lastSkillsUpdated` is set;
 *  - "Not Started" when never updated but an endorsement status exists (endorsedStatus != 0);
 *  - "NA" otherwise.
 */
export function skillsLastUpdatedDisplay(
  lastSkillsUpdated?: string | null,
  endorsedStatus?: number
): string {
  if (lastSkillsUpdated) {
    return formatSkillsDate(lastSkillsUpdated);
  }
  if (endorsedStatus !== undefined && endorsedStatus !== null && endorsedStatus !== 0) {
    return 'Not Started';
  }
  return 'NA';
}

/** Percentage cell color — green at/above 100%, else red (parity: `fgGreen`/`fgRed`). */
export function completionColor(value?: number): string {
  const v = value === undefined || value === null ? 0 : value;
  return v >= 100 ? '#107c10' : '#a4262c';
}
